import { useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { ChevronDown, Link2, Plus, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PaneHeader } from '@/components/settings/pane-header';
import { Switch } from '@/components/settings/settings-controls';
import { SaveBar } from './save-bar';
import { SwimlaneCard } from './swimlane-card';
import { EntryTypesSection } from './entry-types-section';
import { TransitionEventsSection } from './transition-events-section';
import { ConsultationModesSection } from './consultation-modes-section';
import { QueueLinksSection } from './queue-links-section';
import { StagePicker } from './stage-picker';
import { TestsSection } from './tests-section';
import {
  DEFAULT_ENTRY_TYPES,
  DEFAULT_TEST_CATEGORIES,
  DEFAULT_TESTS,
  makeConsultationMode,
  makeEntryType,
  makeQueueTest,
  makeSubCategory,
  makeSwimlane,
  makeTestCategory,
  normalizeQueueDraft,
} from '@/lib/queues';
import { updateQueueDefinition } from '@/lib/api/queues';
import { cn } from '@/lib/utils';

const clone = (value) => JSON.parse(JSON.stringify(value));

const TABS = [
  { id: 'swimlanes', label: 'Swimlanes & sub-categories' },
  { id: 'entry-types', label: 'Entry types' },
  { id: 'transition-events', label: 'Transition events' },
  { id: 'queue-links', label: 'Queue links' },
  { id: 'consultation-modes', label: 'Consultation modes' },
  { id: 'tests', label: 'Tests' },
];

function Field({ label, children }) {
  return (
    <div>
      <div className="mb-1.5 text-[11.5px] font-bold text-muted-foreground">{label}</div>
      {children}
    </div>
  );
}

function ClinicSelect({ value, onChange, disabled, options }) {
  const [open, setOpen] = useState(false);
  const selected = options.find((c) => c.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-left text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Link2 className={cn('h-4 w-4 shrink-0', value ? 'text-primary' : 'text-muted-foreground')} aria-hidden />
          <span className={cn('flex-1 truncate', value ? 'text-foreground' : 'text-muted-foreground')}>
            {selected ? selected.label : 'Unattached (template)'}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-1.5">
        <button
          type="button"
          onClick={() => {
            onChange('');
            setOpen(false);
          }}
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-accent',
            !value ? 'bg-accent font-semibold' : '',
          )}
        >
          <span className="h-2 w-2 rounded-full bg-amber-500" />
          Unattached (template)
        </button>
        {options.length === 0 ? (
          <div className="px-2 py-1.5 text-[12px] italic text-muted-foreground">
            No clinics available
          </div>
        ) : (
          options.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                onChange(c.id);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-accent',
                value === c.id ? 'bg-accent font-semibold' : '',
              )}
            >
              <Link2 className="h-3.5 w-3.5 text-primary" aria-hidden />
              {c.label}
            </button>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
}

// Single-queue editor: one draft of the whole queue definition, tabbed sections editing the same
// draft, and one Save that PUTs the normalized document. Mirrors the thread-groups
// draft → dirty → explicit-Save lifecycle. `queues` is the full tenant list (from the sub-page's
// list state) so the Queue links tab can offer same-clinic targets without extra fetches.
export function QueueEditorPane({ initialQueue, canEdit = true, clinicOptions = [], queues = [], onBack, onSaved }) {
  const [draft, setDraft] = useState(() => clone(initialQueue));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('swimlanes');
  const savedRef = useRef(initialQueue);

  // Same-clinic sibling queues are the only valid link targets; the name map also resolves labels
  // for dangling links (target deleted / moved clinic) so chips can flag them.
  const linkableQueues = queues.filter(
    (q) => q.clinicId && q.clinicId === (draft.clinicId || '') && q.id !== initialQueue.id,
  );
  const queueNameById = new Map(queues.map((q) => [q.id, q.name || 'Untitled queue']));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const editDraft = (next) => {
    setDraft(next);
    setDirty(true);
  };

  const setSwimlanes = (swimlanes) => editDraft({ ...draft, swimlanes });
  const setEntryTypes = (entryTypes) => editDraft({ ...draft, entryTypes });
  const setConsultationModes = (consultationModes) => editDraft({ ...draft, consultationModes });
  const setTestCatalogue = (testCategories, tests) => editDraft({ ...draft, testCategories, tests });

  // Swimlane / sub-category handlers (immutable, addressed by id).
  const addSwimlane = () => setSwimlanes([...draft.swimlanes, makeSwimlane(draft.swimlanes)]);
  const changeSwimlane = (laneId, patch) =>
    setSwimlanes(draft.swimlanes.map((l) => (l.id === laneId ? { ...l, ...patch } : l)));
  const deleteSwimlane = (laneId) => setSwimlanes(draft.swimlanes.filter((l) => l.id !== laneId));
  const reorderSwimlanes = (activeId, overId) => {
    const ids = draft.swimlanes.map((l) => l.id);
    const from = ids.indexOf(activeId);
    const to = ids.indexOf(overId);
    if (from < 0 || to < 0) return;
    setSwimlanes(arrayMove(draft.swimlanes, from, to));
  };
  const addSub = (laneId) =>
    setSwimlanes(
      draft.swimlanes.map((l) =>
        l.id === laneId ? { ...l, subCategories: [...l.subCategories, makeSubCategory(l.subCategories)] } : l,
      ),
    );
  const changeSub = (laneId, subId, patch) =>
    setSwimlanes(
      draft.swimlanes.map((l) =>
        l.id === laneId
          ? { ...l, subCategories: l.subCategories.map((s) => (s.id === subId ? { ...s, ...patch } : s)) }
          : l,
      ),
    );
  const deleteSub = (laneId, subId) =>
    setSwimlanes(
      draft.swimlanes.map((l) =>
        l.id === laneId ? { ...l, subCategories: l.subCategories.filter((s) => s.id !== subId) } : l,
      ),
    );
  const reorderSubs = (laneId, activeId, overId) =>
    setSwimlanes(
      draft.swimlanes.map((l) => {
        if (l.id !== laneId) return l;
        const ids = l.subCategories.map((s) => s.id);
        const from = ids.indexOf(activeId);
        const to = ids.indexOf(overId);
        if (from < 0 || to < 0) return l;
        return { ...l, subCategories: arrayMove(l.subCategories, from, to) };
      }),
    );
  const setEventKeys = (laneId, subId, trigger, keys) =>
    setSwimlanes(
      draft.swimlanes.map((l) =>
        l.id === laneId
          ? {
              ...l,
              subCategories: l.subCategories.map((s) =>
                s.id === subId ? { ...s, events: { ...s.events, [trigger]: keys } } : s,
              ),
            }
          : l,
      ),
    );
  const setSubLink = (laneId, subId, linkedQueue) =>
    setSwimlanes(
      draft.swimlanes.map((l) =>
        l.id === laneId
          ? { ...l, subCategories: l.subCategories.map((s) => (s.id === subId ? { ...s, linkedQueue } : s)) }
          : l,
      ),
    );

  // Doctor-waiting / consultation stage pickers (§1): exactly one sub-category across the WHOLE
  // draft may carry each flag, so selecting a new one clears it everywhere else first. Passing
  // laneId/subId as null (the "None selected" option) just clears the flag everywhere.
  const setStageFlag = (flagKey, laneId, subId) =>
    setSwimlanes(
      draft.swimlanes.map((l) => ({
        ...l,
        subCategories: l.subCategories.map((s) => ({
          ...s,
          [flagKey]: l.id === laneId && s.id === subId,
        })),
      })),
    );

  // Entry-type handlers.
  const addEntryType = () => setEntryTypes([...draft.entryTypes, makeEntryType(draft.entryTypes)]);
  const changeEntryType = (id, patch) =>
    setEntryTypes(draft.entryTypes.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  const deleteEntryType = (id) => setEntryTypes(draft.entryTypes.filter((t) => t.id !== id));
  const reorderEntryTypes = (activeId, overId) => {
    const ids = draft.entryTypes.map((t) => t.id);
    const from = ids.indexOf(activeId);
    const to = ids.indexOf(overId);
    if (from < 0 || to < 0) return;
    setEntryTypes(arrayMove(draft.entryTypes, from, to));
  };
  const resetEntryTypes = () =>
    setEntryTypes(DEFAULT_ENTRY_TYPES.map((t, i) => ({ ...t, order: i })));

  // Consultation-mode handlers (mirrors the entry-type handlers above).
  const draftConsultationModes = Array.isArray(draft.consultationModes) ? draft.consultationModes : [];
  const addConsultationMode = () =>
    setConsultationModes([...draftConsultationModes, makeConsultationMode(draftConsultationModes)]);
  const changeConsultationMode = (id, patch) =>
    setConsultationModes(draftConsultationModes.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  const deleteConsultationMode = (id) =>
    setConsultationModes(draftConsultationModes.filter((m) => m.id !== id));
  const reorderConsultationModes = (activeId, overId) => {
    const ids = draftConsultationModes.map((m) => m.id);
    const from = ids.indexOf(activeId);
    const to = ids.indexOf(overId);
    if (from < 0 || to < 0) return;
    setConsultationModes(arrayMove(draftConsultationModes, from, to));
  };

  // Orderable-test catalogue handlers. Tests are nested under categories, so deleting a category
  // takes its tests with it — leaving them behind would file them under a heading that no longer
  // exists, and the doctor's sheet would silently drop them.
  const draftTestCategories = Array.isArray(draft.testCategories) ? draft.testCategories : [];
  const draftTests = Array.isArray(draft.tests) ? draft.tests : [];
  const addTestCategory = () =>
    setTestCatalogue([...draftTestCategories, makeTestCategory(draftTestCategories)], draftTests);
  const changeTestCategory = (id, patch) =>
    setTestCatalogue(
      draftTestCategories.map((c) => (c.id === id ? { ...c, ...patch } : c)),
      draftTests,
    );
  const deleteTestCategory = (id) =>
    setTestCatalogue(
      draftTestCategories.filter((c) => c.id !== id),
      draftTests.filter((t) => t.categoryId !== id),
    );
  const addTest = (categoryId) =>
    setTestCatalogue(draftTestCategories, [...draftTests, makeQueueTest(categoryId, draftTests)]);
  const changeTest = (id, patch) =>
    setTestCatalogue(
      draftTestCategories,
      draftTests.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    );
  const deleteTest = (id) =>
    setTestCatalogue(
      draftTestCategories,
      draftTests.filter((t) => t.id !== id),
    );
  const resetTestCatalogue = () =>
    setTestCatalogue(
      DEFAULT_TEST_CATEGORIES.map((c, i) => ({ ...c, order: i })),
      DEFAULT_TESTS.map((t, i) => ({ ...t, order: i })),
    );

  const handleLaneDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) reorderSwimlanes(active.id, over.id);
  };

  const handleSave = async () => {
    if (saving) return;
    const body = normalizeQueueDraft(draft);
    if (!body.name) {
      toast.error('Queue name is required.');
      return;
    }
    setSaving(true);
    try {
      const updated = await updateQueueDefinition({ queueId: initialQueue.id, body });
      savedRef.current = updated;
      setDraft(clone(updated));
      setDirty(false);
      onSaved?.(updated);
      toast.success('Queue saved for everyone.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save queue');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setDraft(clone(savedRef.current));
    setDirty(false);
  };

  const swimlanes = Array.isArray(draft.swimlanes) ? draft.swimlanes : [];
  const consultationModes = Array.isArray(draft.consultationModes) ? draft.consultationModes : [];
  const laneColorIds = (laneId) =>
    new Set(swimlanes.filter((l) => l.id !== laneId).map((l) => l.colorId));

  const leftActions =
    tab === 'swimlanes' ? (
      <Button type="button" variant="outline" size="sm" onClick={addSwimlane} disabled={!canEdit}>
        <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
        Add swimlane
      </Button>
    ) : tab === 'entry-types' ? (
      <Button type="button" variant="ghost" size="sm" onClick={resetEntryTypes} disabled={!canEdit}>
        <RotateCcw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
        Reset to defaults
      </Button>
    ) : tab === 'consultation-modes' ? (
      <Button type="button" variant="outline" size="sm" onClick={addConsultationMode} disabled={!canEdit}>
        <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
        Add mode
      </Button>
    ) : tab === 'queue-links' ? (
      <span className="text-[12px] text-muted-foreground">
        Queue links are saved on the queue with everything else.
      </span>
    ) : (
      <span className="text-[12px] text-muted-foreground">
        Transition events are saved on the queue with everything else.
      </span>
    );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PaneHeader
        crumb={[{ label: 'Queues', onClick: onBack }, { label: draft.name || 'Untitled queue' }]}
        title="Edit queue"
        action={
          <label className="inline-flex items-center gap-2 text-[13px] font-semibold text-foreground">
            Active
            <Switch
              checked={Boolean(draft.active)}
              onChange={(v) => editDraft({ ...draft, active: v })}
              label="Queue active"
              disabled={!canEdit}
            />
          </label>
        }
      />

      <ScrollArea className="min-h-0 flex-1">
        <div className="mx-auto flex max-w-4xl flex-col gap-5 p-6">
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="flex-1 space-y-3">
              <Field label="Queue name">
                <Input
                  value={draft.name}
                  onChange={(e) => editDraft({ ...draft, name: e.target.value })}
                  maxLength={80}
                  disabled={!canEdit}
                  className="h-9 text-sm font-semibold"
                  placeholder="Queue name"
                />
              </Field>
              <Field label="Description">
                <Input
                  value={draft.description}
                  onChange={(e) => editDraft({ ...draft, description: e.target.value })}
                  maxLength={200}
                  disabled={!canEdit}
                  className="h-9 text-sm"
                  placeholder="What this queue is for"
                />
              </Field>
            </div>
            <div className="w-full space-y-2 md:w-80">
              <Field label="Attached clinic">
                <ClinicSelect
                  value={draft.clinicId || ''}
                  onChange={(id) => editDraft({ ...draft, clinicId: id })}
                  disabled={!canEdit}
                  options={clinicOptions}
                />
              </Field>
              <div className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2">
                <span className="text-[13px] font-semibold text-foreground">Direct check-in</span>
                <Switch
                  checked={draft.directCheckIn !== false}
                  onChange={(v) => editDraft({ ...draft, directCheckIn: v })}
                  label="Direct check-in"
                  disabled={!canEdit}
                />
              </div>
              <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                A clinic can run several active queues, ordered from the queues list — the first one
                is the primary queue new entries land in. Direct check-in queues get their own
                check-in buttons in the staff app; others receive patients only through queue links
                and transfers. Leave unattached to save a template.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 border-b border-border">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={cn(
                  '-mb-px border-b-2 px-3 py-2 text-[13px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  tab === t.id
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'swimlanes' ? (
            <div className="space-y-4">
              <div className="space-y-2.5 rounded-lg border border-border bg-card px-4 py-3">
                <span className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">
                  Key stages
                </span>
                <StagePicker
                  label="Doctor-waiting stage"
                  hint="Sub-category patients are in while waiting for the doctor"
                  swimlanes={swimlanes}
                  flagKey="isDoctorWaitingStage"
                  disabled={!canEdit}
                  onSelect={(laneId, subId) => setStageFlag('isDoctorWaitingStage', laneId, subId)}
                />
                <StagePicker
                  label="Consultation stage"
                  hint="Sub-category patients are in during the consultation"
                  swimlanes={swimlanes}
                  flagKey="isConsultationStage"
                  disabled={!canEdit}
                  onSelect={(laneId, subId) => setStageFlag('isConsultationStage', laneId, subId)}
                />
                <StagePicker
                  label="Post-visit stage"
                  hint="Sub-category patients move to once the doctor ends their visit"
                  swimlanes={swimlanes}
                  flagKey="isPostVisitStage"
                  disabled={!canEdit}
                  onSelect={(laneId, subId) => setStageFlag('isPostVisitStage', laneId, subId)}
                />
                <StagePicker
                  label="Awaiting-tests stage"
                  hint="Sub-category patients move to once the doctor orders additional tests"
                  swimlanes={swimlanes}
                  flagKey="isAwaitingTestsStage"
                  disabled={!canEdit}
                  onSelect={(laneId, subId) => setStageFlag('isAwaitingTestsStage', laneId, subId)}
                />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[13px] font-extrabold tracking-wide text-foreground">SWIMLANES</span>
                <span className="h-px flex-1 bg-border" />
                <Button type="button" variant="outline" size="sm" onClick={addSwimlane} disabled={!canEdit}>
                  <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  Add swimlane
                </Button>
              </div>
              <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleLaneDragEnd}>
                <SortableContext items={swimlanes.map((l) => l.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-3">
                    {swimlanes.map((lane, laneIndex) => (
                      <SwimlaneCard
                        key={lane.id}
                        lane={lane}
                        isFirst={laneIndex === 0}
                        laneColorIds={laneColorIds(lane.id)}
                        queueNameById={queueNameById}
                        disabled={!canEdit}
                        onChange={(patch) => changeSwimlane(lane.id, patch)}
                        onDelete={() => deleteSwimlane(lane.id)}
                        onAddSub={() => addSub(lane.id)}
                        onChangeSub={(subId, patch) => changeSub(lane.id, subId, patch)}
                        onDeleteSub={(subId) => deleteSub(lane.id, subId)}
                        onReorderSubs={(activeId, overId) => reorderSubs(lane.id, activeId, overId)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              {swimlanes.length === 0 ? (
                <div className="rounded-md border border-dashed border-border px-3 py-8 text-center text-sm text-muted-foreground">
                  No swimlanes yet. Add one to start building the board.
                </div>
              ) : null}
            </div>
          ) : tab === 'entry-types' ? (
            <EntryTypesSection
              entryTypes={Array.isArray(draft.entryTypes) ? draft.entryTypes : []}
              disabled={!canEdit}
              onChange={changeEntryType}
              onDelete={deleteEntryType}
              onAdd={addEntryType}
              onReorder={reorderEntryTypes}
            />
          ) : tab === 'consultation-modes' ? (
            <ConsultationModesSection
              consultationModes={consultationModes}
              disabled={!canEdit}
              onChange={changeConsultationMode}
              onDelete={deleteConsultationMode}
              onAdd={addConsultationMode}
              onReorder={reorderConsultationModes}
            />
          ) : tab === 'tests' ? (
            <TestsSection
              testCategories={draftTestCategories}
              tests={draftTests}
              disabled={!canEdit}
              onChangeCategory={changeTestCategory}
              onDeleteCategory={deleteTestCategory}
              onAddCategory={addTestCategory}
              onChangeTest={changeTest}
              onDeleteTest={deleteTest}
              onAddTest={addTest}
              onResetCatalogue={resetTestCatalogue}
            />
          ) : tab === 'queue-links' ? (
            <QueueLinksSection
              swimlanes={swimlanes}
              linkableQueues={linkableQueues}
              queueNameById={queueNameById}
              clinicAttached={Boolean(draft.clinicId)}
              disabled={!canEdit}
              onSetLink={setSubLink}
            />
          ) : (
            <TransitionEventsSection swimlanes={swimlanes} disabled={!canEdit} onSetEventKeys={setEventKeys} />
          )}
        </div>
      </ScrollArea>

      <SaveBar
        dirty={dirty}
        saving={saving}
        canSave={canEdit}
        onSave={handleSave}
        onDiscard={handleDiscard}
        leftActions={leftActions}
      />
    </div>
  );
}
